import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";
import eyeOpen from '../../../images/icon-eye-open.svg';
import eyeClose from '../../../images/icon-eye-close.svg';
import style from './ChangePassword.module.css'
import { REGEX } from '../../../constants/constants';
import { AppDispatch } from '../../../redux/rootReducer';
import { useDispatch } from 'react-redux';
import { ChangePasswordProps, changeAdminPassword } from '../../../redux/auth/actions/changePassword.action';
import Toast from '../../common/toast/Toast';
import TinyLoader from '../../common/tiny-loader/TinyLoader';
import toast from 'react-hot-toast';

const ChangePassword = () => {

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const {
        handleSubmit,
        setValue,
        getValues,
        formState: { isValid, errors },
        control,
        trigger,
        reset,
    } = useForm({
        defaultValues: {
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
        mode: "onChange",
    });

    const [showPass, setShowPass] = useState<boolean>(false);
    const [showNewPass, setShowNewPass] = useState<boolean>(false);
    const [showConfirmPass, setShowConfirmPass] = useState<boolean>(false);

    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState('');
    const [toastMessage, setToastMessage] = useState('');

    const onSuccess = (message: string) => {
        setIsLoading(false);
        toast.success(message);
        navigate('/')
        localStorage.clear();
        reset();
    }

    const onFailure = (errMsg: string) => {
        setShowToast(true);
        setToastType('error');
        setToastMessage(errMsg);
        setIsLoading(false);
    }


    const onSubmit = (data: { oldPassword: string, confirmPassword: string, newPassword: string }) => {
        setIsLoading(true);
        const changePasswordObj: ChangePasswordProps = {
            currentPassword: data?.oldPassword,
            newPassword: data?.newPassword,
            confirmedPassword: data?.confirmPassword
        }

        dispatch(changeAdminPassword(changePasswordObj, onSuccess, onFailure));
    }

    useEffect(() => {
        if (showToast) {
            setTimeout(() => {
                setShowToast(false);
            }, 4000);
        }
    }, [showToast])

    return (
        <div className={style.changePasswordContainer}>
            <div className={style.innerContainer}>
                <form onSubmit={handleSubmit(onSubmit)} className={style.formWrapper} noValidate>
                    {/* Form Header */}
                    <h2 className={style.h2}>Change Password</h2>

                    {/* Current Password Field */}
                    <Controller
                        name="oldPassword"
                        control={control}
                        rules={{
                            required: "Current password is required"
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label>Current Password</label>
                                    <div className={style.passwordWrapper}>
                                        <input
                                            value={value}
                                            onChange={(e) => onChange(e)}
                                            type={showPass ? 'text' : 'password'}
                                            id="oldPassword"
                                            name="oldPassword"
                                            placeholder="Enter your current password"
                                        />
                                        <div onClick={() => setShowPass(!showPass)} className={style.iconToggle}>
                                            <img src={!showPass ? eyeClose : eyeOpen} alt="Toggle password visibility" />
                                        </div>
                                    </div>
                                    {errors?.oldPassword && <p className={style.invalidCred}>{errors?.oldPassword?.message}</p>}
                                </div>
                            )
                        }}
                    />

                    {/* New Password Field */}
                    <Controller
                        name="newPassword"
                        control={control}
                        rules={{
                            required: "New password is required",
                            pattern: {
                                value: REGEX.PASSWORD,
                                message:
                                    "The password must contain at least one character, one number, one special character, and be between 8 and 12 characters in length",
                            },
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={`${style.fieldWrapper} ${style.newPasswordFieldWrapper}`}>
                                    <label>New Password</label>
                                    <div className={style.passwordWrapper}>
                                        <input
                                            value={value}
                                            onChange={(e) => {
                                                onChange(e);
                                                setValue("newPassword", e.target.value);
                                                getValues("confirmPassword") !== "" &&
                                                    trigger("confirmPassword");
                                            }}
                                            type={showNewPass ? 'text' : 'password'}
                                            id="newPassword"
                                            name="newPassword"
                                            placeholder="Enter new password"
                                        />
                                        <div onClick={() => setShowNewPass(!showNewPass)} className={style.iconToggle}>
                                            <img src={!showNewPass ? eyeClose : eyeOpen} alt="Toggle password visibility" />
                                        </div>
                                    </div>
                                    {errors?.newPassword && <p className={style.invalidCred}>{errors?.newPassword?.message}</p>}
                                </div>
                            )
                        }}
                    />

                    <Controller
                        name="confirmPassword"
                        control={control}
                        rules={{
                            required: "Confirm password is required",
                            validate: (value) =>
                                value == getValues("newPassword") || "The passwords do not match",
                        }}
                        render={({ field: { value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label>Password</label>
                                    <div className={style.passwordWrapper}>
                                        <input
                                            value={value}
                                            type={showConfirmPass ? 'text' : 'password'}
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            placeholder="Confirm new password"
                                            onChange={(e) => {
                                                setValue("confirmPassword", e.target.value, {
                                                    shouldValidate:
                                                        getValues("newPassword") !== "" ? true : false,
                                                });
                                            }}
                                        />
                                        <div onClick={() => setShowConfirmPass(!showConfirmPass)} className={style.iconToggle}>
                                            <img src={!showConfirmPass ? eyeClose : eyeOpen} alt="Eye" />
                                        </div>
                                    </div>
                                    {errors?.confirmPassword && <p className={style.invalidCred}>{errors?.confirmPassword?.message}</p>}
                                </div>
                            )
                        }}
                    />

                    <div className={style.buttonContainer}>

                        <div className={style.cancelWrapper}>
                            <button type="button" onClick={() => { reset(); navigate('/locations'); }}>Cancel</button>
                        </div>

                        <div className={style.submitWrapper}>
                            <button type="submit" disabled={!isValid || isLoading}>{
                                isLoading ? <span className={style.loader}><TinyLoader /></span> : <span>Update</span>
                            }</button>
                        </div>
                    </div>


                </form>
            </div>
            {showToast && <Toast type={toastType} message={toastMessage} />}
        </div>
    )
}

export default ChangePassword